// Decides what the Google reviews summary line may claim.
//
// The rule this file exists to enforce: an average or a review count may only be
// rendered when it came from Google. It is never computed from the review cards
// on screen, and it is never defaulted to zero.
//
// Place Details returns at most five reviews, so a mean over the visible cards
// describes the sample rather than the business. The site once printed "Based on
// 6 Google reviews" beside a five-star average on a profile with a thousand
// reviews. That computation is the defect; keeping the decision in one pure
// function is what makes it hard to reintroduce by accident.
//
// Both inputs Google can supply are accepted, and they are the same shape:
//   - the live Places lookup, fetched this page load (utils/googlePlaces.js)
//   - the backend's stored aggregate, the bundle's `review_meta` (ASAP-RVS-005)

/** A rating outside (0, 5] cannot have come from Google. */
function isUsableRating(value) {
  const rating = Number(value);
  return Number.isFinite(rating) && rating > 0 && rating <= 5;
}

/** A count has to be a positive whole number to be worth printing. */
function isUsableTotal(value) {
  const total = Number(value);
  return Number.isFinite(total) && total > 0;
}

/**
 * @param {object|null|undefined} meta `{ rating, totalReviews, capturedAt }`, or
 *   nothing at all.
 * @returns {{rating: number|null, totalReviews: number|null,
 *            capturedAt: string|null, showRating: boolean, showTotal: boolean,
 *            showSummary: boolean}}
 *   `showSummary` false means the aggregate line is omitted from the page
 *   entirely. Either figure may stand alone; both absent means nothing is shown.
 */
export function resolveAggregate(meta) {
  const rating = isUsableRating(meta?.rating) ? Number(meta.rating) : null;
  const totalReviews = isUsableTotal(meta?.totalReviews)
    ? Math.round(Number(meta.totalReviews))
    : null;

  const showRating = rating !== null;
  const showTotal = totalReviews !== null;

  return {
    rating,
    totalReviews,
    capturedAt: typeof meta?.capturedAt === 'string' ? meta.capturedAt : null,
    showRating,
    showTotal,
    showSummary: showRating || showTotal,
  };
}

export default resolveAggregate;